/**
 * Where a logged-out visitor to an admin route goes (spec 2026-10-15). The
 * one login page replaced the old root chooser; its forward survives: a visitor
 * holding a portal session lands in that portal, as before.
 */
import { validateRedirectPath } from "@/config/auth";
import {
  listRefreshablePortalSessions,
  PortalSessionSummary,
} from "@/core/auth/listPortalSessions";
import { portalSurfacePath } from "@/core/auth/portalSurface";
import { clearPortalAuth, getPortalTokenKey } from "@/core/auth/storage";
import type { User } from "@/types";

export type GuardState = "in" | "refreshing" | "out";

/**
 * Whether the admin guard lets the visitor in. An access token lives an hour
 * and the refresh token seven days, so an expired access token with a refresh
 * token is refreshing, not logged out: useTokenValidation refreshes it in the
 * background, and a failed refresh clears the user, which reads as "out".
 */
export function guardState(
  user: Pick<User, "accessToken" | "refreshToken"> | null,
  isAuthenticated: boolean
): GuardState {
  if (!user) return "out";
  if (isAuthenticated) return "in";
  // The background refresh only runs for a user holding an access token.
  return user.accessToken && user.refreshToken ? "refreshing" : "out";
}

/**
 * Refreshable portal sessions, clearing truly dead ones (no valid token and
 * no refresh token) along the way. Expired-but-refreshable sessions stay:
 * the target surface refreshes them (TD-016).
 */
export function refreshablePortalSessionsWithCleanup(): PortalSessionSummary[] {
  const refreshable = listRefreshablePortalSessions();
  try {
    const prefix = "touchpoint_portal_token_";
    const alive = new Set(refreshable.map((s) => s.clientId));
    const staleClientIds: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const clientId = key.slice(prefix.length);
      if (!clientId || key !== getPortalTokenKey(clientId)) continue;
      if (alive.has(clientId)) continue;
      staleClientIds.push(clientId);
    }
    staleClientIds.forEach(clearPortalAuth);
  } catch {
    // localStorage unavailable: nothing to clean.
  }
  return refreshable;
}

export function loggedOutTarget(
  pathname: string,
  search: string,
  sessions: PortalSessionSummary[],
  signedOutByUser = false
): string {
  // An explicit sign-out never forwards into a portal session.
  if (signedOutByUser) return "/portal/login";
  const inbound = validateRedirectPath(new URLSearchParams(search).get("redirect"));
  if (sessions.length > 0) {
    return inbound ?? portalSurfacePath(sessions[0].role, sessions[0].clientId);
  }
  if (inbound) return `/portal/login?redirect=${encodeURIComponent(inbound)}`;
  if (pathname === "/") return "/portal/login";
  return `/portal/login?redirect=${encodeURIComponent(pathname + search)}`;
}
